"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { FormattedLocation, locationValue, useLocationSearch } from "@/hooks/useLocationSearch";
import { FetchMultiselect, MultiSelectOption } from "@/fetch-ui/FetchMultiselect";
import { FetchSearchableDropdown } from "@/fetch-ui/FetchSearchableDropdown";
import { PLACE_FIELD_MASKS } from "@/lib/database/server/place-field-masks";
import { autocompletePlaces, getPlaceDetails } from "@/lib/database/server/place-actions";
import { useDebounce } from "@/hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";

interface LocationAutocompleteProps {
  searchType: "city" | "address";
  selectedLocations: string[];
  onLocationChange: (selectedOptions: string[], formattedLocations: FormattedLocation[]) => void;
  formattedLocations: FormattedLocation[];
  limit?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  height?: string;
  status?: "default" | "error";
  onSearchingChange?: (isSearching: boolean) => void;
  showPills?: boolean;
  isNowadaysAdmin?: boolean;
}

interface LocationOptionWithPlaceId {
  label: string;
  value: string;
  place_id: string;
}

interface AutocompleteSuggestion {
  placePrediction: {
    place: string;
    placeId: string;
    text: {
      text: string;
      matches: Array<{ endOffset: number }>;
    };
    structuredFormat: {
      mainText: { text: string };
      secondaryText: { text: string };
    };
  };
}

export function LocationAutocomplete({
  searchType,
  selectedLocations,
  onLocationChange,
  formattedLocations,
  limit,
  placeholder,
  disabled = false,
  className = "",
  height,
  status,
  onSearchingChange,
  showPills = true,
  isNowadaysAdmin = false,
}: LocationAutocompleteProps) {
  const isSingleSelect = limit === 1;
  const { isGoogleMapsLoaded } = useGoogleMaps();
  const {
    locationOptions: citySearchOptions,
    isSearching: isCitySearching,
    searchLocations,
  } = useLocationSearch(isGoogleMapsLoaded && searchType === "city");

  const [addressQuery, setAddressQuery] = useState<string>("");
  const [isAddressSearching, setIsAddressSearching] = useState<boolean>(false);
  const [addressResults, setAddressResults] = useState<AutocompleteSuggestion[]>([]);
  const debouncedAddressQuery = useDebounce(addressQuery, 500);

  const sessionToken = useMemo(() => `session_${Date.now()}_${Math.random().toString(36).substring(7)}`, []);

  const isSearching = searchType === "city" ? isCitySearching : isAddressSearching;
  const effectiveLimit = limit ?? Infinity;
  const canAddMore = selectedLocations.length < effectiveLimit;

  useEffect(() => {
    onSearchingChange?.(isSearching);
  }, [isSearching, onSearchingChange]);

  useEffect(() => {
    if (searchType !== "address") {
      setAddressResults([]);
      setIsAddressSearching(false);
      return;
    }

    if (debouncedAddressQuery.length < 2) {
      setAddressResults([]);
      return;
    }

    const search = async () => {
      setIsAddressSearching(true);
      try {
        const data = await autocompletePlaces(debouncedAddressQuery, sessionToken);
        setAddressResults(data.suggestions || []);
      } catch (error) {
        console.error("Error searching places:", error);
        setAddressResults([]);
      } finally {
        setIsAddressSearching(false);
      }
    };
    search();
  }, [debouncedAddressQuery, sessionToken, searchType]);

  const existingOptions: LocationOptionWithPlaceId[] = formattedLocations.map((loc) => ({
    label: loc.display_name || locationValue(loc),
    value: locationValue(loc),
    place_id: loc.place_id,
  }));

  const addressOptions: LocationOptionWithPlaceId[] =
    searchType === "address"
      ? addressResults.map((suggestion) => ({
          label: suggestion.placePrediction.text.text,
          value: suggestion.placePrediction.text.text,
          place_id: suggestion.placePrediction.placeId,
        }))
      : [];

  const cityOptions: LocationOptionWithPlaceId[] = searchType === "city" ? citySearchOptions : [];

  const allLocationOptions = [...existingOptions, ...cityOptions, ...addressOptions];

  const allOptions: MultiSelectOption[] = Array.from(
    new Map(allLocationOptions.map((opt) => [opt.value, { label: opt.label, value: opt.value }])).values()
  );

  const locationMetadata = new Map<string, LocationOptionWithPlaceId>(
    allLocationOptions.map((opt) => [opt.value, opt])
  );

  const handleSearchChange = (searchTerm: string) => {
    if (searchType === "city") {
      if (searchTerm.length >= 2 && isGoogleMapsLoaded) {
        searchLocations(searchTerm);
      }
    } else {
      setAddressQuery(searchTerm);
    }
  };

  const handleChange = async (selectedValues: string[]) => {
    if (selectedValues.length > effectiveLimit && !isNowadaysAdmin) return;

    const existingAddresses = new Set(formattedLocations.map(locationValue));
    const newSelections = selectedValues.filter((opt) => !existingAddresses.has(opt));
    const keptLocations = formattedLocations.filter((loc) => selectedValues.includes(locationValue(loc)));

    if (newSelections.length === 0) {
      onLocationChange(selectedValues, keptLocations);
      return;
    }

    const pendingLocations: FormattedLocation[] = newSelections.map((value) => ({
      city: value.split(",")[0]?.trim() || "",
      state: null,
      country: null,
      formatted_address: value,
      display_name: searchType === "address" ? value : undefined,
      place_id: locationMetadata.get(value)?.place_id || "",
      coordinates: { lat: 0, lng: 0 },
      type: searchType,
      pending: true,
    }));

    onLocationChange(selectedValues, [...keptLocations, ...pendingLocations]);

    const fetchPlaceDetails = async (value: string): Promise<FormattedLocation | null> => {
      const opt = locationMetadata.get(value);
      if (!opt?.place_id) return null;

      try {
        const details = await getPlaceDetails(opt.place_id, [
          PLACE_FIELD_MASKS.ESSENTIALS.addressComponents,
          PLACE_FIELD_MASKS.ESSENTIALS.formattedAddress,
          PLACE_FIELD_MASKS.ESSENTIALS.location,
        ]);

        const addressComponents = details.addressComponents || [];
        const findComponent = (types: string[]) =>
          addressComponents.find((c: any) => types.some((type) => c.types?.includes(type)))?.longText || null;

        return {
          city: findComponent(["locality", "sublocality_level_1", "sublocality"]) || value.split(",")[0]?.trim() || "",
          state: findComponent(["administrative_area_level_1"]),
          country: findComponent(["country"]),
          formatted_address: value,
          full_address: details.formattedAddress || undefined,
          display_name: searchType === "address" ? value : undefined,
          place_id: opt.place_id,
          coordinates: details.location
            ? { lat: details.location.latitude || 0, lng: details.location.longitude || 0 }
            : { lat: 0, lng: 0 },
          type: searchType,
        };
      } catch (error) {
        console.error(`Error fetching place details for ${opt.place_id}:`, error);
        return null;
      }
    };

    const newLocationData = await Promise.all(newSelections.map(fetchPlaceDetails));
    const resolvedLocations = newLocationData.filter((loc): loc is FormattedLocation => loc !== null);

    const resolvedAddresses = new Set(resolvedLocations.map((loc) => loc.formatted_address));
    const fallbackLocations = pendingLocations
      .filter((loc) => !resolvedAddresses.has(loc.formatted_address))
      .map(({ pending, ...rest }) => rest);

    onLocationChange(selectedValues, [...keptLocations, ...resolvedLocations, ...fallbackLocations]);
  };

  const defaultPlaceholder =
    searchType === "city"
      ? "Please select the event locations... (type at least 2 characters)"
      : "Search for a place or address...";

  if (isSingleSelect) {
    return (
      <div className={className} style={height ? { height } : undefined}>
        <FetchSearchableDropdown
          id="location-select"
          options={allOptions}
          value={selectedLocations[0] || ""}
          onChange={(value) => handleChange(value ? [value] : [])}
          placeholder={placeholder || defaultPlaceholder}
          status={status}
          clearable={true}
          disabled={disabled}
          className={className}
          onSearchChange={handleSearchChange}
          loading={isSearching}
          noOptionsMessage={isSearching ? "Loading..." : "No matches found"}
        />
      </div>
    );
  }

  return (
    <div className={className} style={height ? { height } : undefined}>
      <FetchMultiselect
        options={allOptions}
        selectedValues={selectedLocations}
        onChange={handleChange}
        placeholder={placeholder || defaultPlaceholder}
        searchable={true}
        showPills={showPills}
        onSearchChange={handleSearchChange}
        disableClientFilter={true}
        loading={isSearching}
        disabled={disabled}
      />
    </div>
  );
}
